import React, { useMemo, useState, useEffect, useRef } from 'react';
import { getUserInfo } from '../utils/tokenManager';
import { getAuthHeader } from '../utils/tokenManager';
import { getContentMainList, getItineraryType, getItineraryDetail, uploadS3 } from '../services/apiService';
import { buildTravelProductHTMLElement, generatePdfBlobFromElement } from '../utils/pdfExportUtil';

export default function QuotePreviewModal({ open, onClose, data, includeCost = false, productNo, productName, onAttachmentAdded, onOpenEmail }) {
  if (!open) return null;
  const [showCost, setShowCost] = useState(!!includeCost);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [includeProductDetail, setIncludeProductDetail] = useState(false);
  const [productDetailLoading, setProductDetailLoading] = useState(false);
  const [productDetailError, setProductDetailError] = useState('');
  const productDetailContainerRef = useRef(null);
  const [attaching, setAttaching] = useState(false);
  const [emailProcessing, setEmailProcessing] = useState(false);
  const [htmlAttaching, setHtmlAttaching] = useState(false);

  const AUTO_QUOTE_PREFIX = 'TRP__';

  const ATTACH_PATH = '1/counseling/Schedule/';
  const makeTimestampedName = (name) => {
    try {
      const dot = name.lastIndexOf('.');
      const base = dot > -1 ? name.slice(0, dot) : name;
      const ext = dot > -1 ? name.slice(dot) : '';
      const d = new Date();
      const pad = (n)=> String(n).padStart(2,'0');
      const ts = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
      return `${base}_${ts}${ext}`;
    } catch { return name; }
  };

  const inlineComputedStyles = (rootEl) => {
    try {
      if (!rootEl) return;
      const applyStyle = (el) => {
        try {
          const computed = window.getComputedStyle(el);
          if (!computed) return;
          const prev = el.getAttribute('style') || '';
          let cssText = '';
          const tag = String(el?.tagName || '').toUpperCase();
          const keepSize = tag === 'IMG' || tag === 'SVG' || tag === 'CANVAS' || tag === 'VIDEO';
          for (let i = 0; i < computed.length; i++) {
            const prop = computed[i];
            if (!keepSize && (prop === 'height' || prop === 'min-height' || prop === 'max-height')) continue;
            cssText += `${prop}:${computed.getPropertyValue(prop)};`;
          }
          el.setAttribute('style', `${prev};${cssText}`);
          el.removeAttribute('class');
        } catch {}
      };

      applyStyle(rootEl);
      const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_ELEMENT);
      while (walker.nextNode()) {
        applyStyle(walker.currentNode);
      }
    } catch {}
  };

  const buildStandaloneEditableHtml = (contentEl, { title } = {}) => {
    const safeTitle = String(title || '견적서');
    const wrapper = document.createElement('div');
    wrapper.id = 'quote-export-root';
    wrapper.style.maxWidth = '794px';
    wrapper.style.margin = '0 auto';
    wrapper.style.padding = '20px';
    wrapper.style.background = '#ffffff';
    wrapper.style.width = '100%';
    try {
      wrapper.setAttribute('contenteditable', 'true');
      wrapper.setAttribute('spellcheck', 'false');
    } catch {}

    const cloned = contentEl.cloneNode(true);
    try {
      // Absolutize asset URLs so they keep working when the HTML is opened from another origin.
      const absolutizeUrl = (raw) => {
        try {
          const v = String(raw || '').trim();
          if (!v) return '';
          if (/^(https?:)?\/\//i.test(v) || /^data:/i.test(v) || /^blob:/i.test(v)) return v;
          return new URL(v, window.location.href).toString();
        } catch {
          return String(raw || '');
        }
      };
      cloned.querySelectorAll('img[src]').forEach((img) => {
        try {
          const src = img.getAttribute('src');
          img.setAttribute('src', absolutizeUrl(src));
        } catch {}
      });
      cloned.querySelectorAll('a[href]').forEach((a) => {
        try {
          const href = a.getAttribute('href');
          if (!href) return;
          if (href.startsWith('#') || href.startsWith('javascript:')) return;
          a.setAttribute('href', absolutizeUrl(href));
        } catch {}
      });
    } catch {}
    wrapper.appendChild(cloned);

    // Important: computed styles are only reliable when attached to the document.
    wrapper.style.position = 'fixed';
    wrapper.style.left = '-9999px';
    wrapper.style.top = '0';
    document.body.appendChild(wrapper);
    try {
      inlineComputedStyles(wrapper);
      try {
        const editableEls = Array.from(wrapper.querySelectorAll('[data-editable="true"]'));
        editableEls.forEach((el) => {
          try {
            el.style.setProperty('display', 'block', 'important');
            el.style.setProperty('white-space', 'pre-wrap', 'important');
            el.style.setProperty('height', 'auto', 'important');
            el.style.setProperty('max-height', 'none', 'important');
            el.style.setProperty('overflow', 'visible', 'important');
            if (!el.style.minHeight) el.style.setProperty('min-height', '20px', 'important');
          } catch {}

          try {
            let p = el.parentElement;
            while (p && p !== wrapper) {
              try {
                p.style.setProperty('height', 'auto', 'important');
                p.style.setProperty('max-height', 'none', 'important');
                p.style.setProperty('min-height', '0px', 'important');
              } catch {}
              p = p.parentElement;
            }
          } catch {}
        });
      } catch {}
    } finally {
      try { document.body.removeChild(wrapper); } catch {}
      try {
        wrapper.style.position = '';
        wrapper.style.left = '';
        wrapper.style.top = '';
      } catch {}
    }

    const bodyHtml = wrapper.outerHTML;
    return `<!DOCTYPE html>
<html lang="ko">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${safeTitle.replace(/</g, '&lt;')}</title>
    <style>
      html, body { 
        margin: 0; 
        padding: 0; 
        background: #ffffff; 
        color: #111827; 
        font-family: Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Malgun Gothic', Arial, sans-serif;
        width: 100%;
      }
      #quote-export-root [data-editable="true"] { height: auto !important; max-height: none !important; overflow: visible !important; white-space: pre-wrap !important; }
      #quote-export-toolbar { position: sticky; top: 0; z-index: 9999; background: #111827; color: #fff; padding: 10px 12px; display: flex; gap: 8px; align-items: center; }
      #quote-export-toolbar button { background: #2563eb; border: 0; color: #fff; padding: 8px 10px; border-radius: 6px; font-size: 12px; cursor: pointer; }
      #quote-export-toolbar button.secondary { background: #374151; }
      #quote-export-toolbar .hint { margin-left: auto; font-size: 12px; opacity: 0.9; }
      [data-editable="true"] { outline: 2px dashed rgba(37,99,235,0.35); outline-offset: 2px; }
      [contenteditable="true"] { cursor: text; }
      @media print {
        @page { margin: 1cm; }
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
        }
        #quote-export-toolbar { display: none !important; }
        [data-editable="true"] { outline: none !important; }
        #quote-export-root { 
          padding: 0 !important; 
          max-width: none !important;
          width: 100% !important;
        }
      }
    </style>
  </head>
  <body>
    <div id="quote-export-toolbar">
      <button id="btn-toggle-edit" class="secondary" type="button">편집 잠금</button>
      <button id="btn-print" type="button">인쇄</button>
      <div class="hint">텍스트 수정 후 인쇄하세요. (Ctrl+P)</div>
    </div>
    ${bodyHtml}
    <script>
      (function(){
        var unlocked = true;
        var root = document.getElementById('quote-export-root');
        function setEditable(flag){
          unlocked = !!flag;
          if (root) root.setAttribute('contenteditable', unlocked ? 'true' : 'false');
          var btn = document.getElementById('btn-toggle-edit');
          if (btn) btn.textContent = unlocked ? '편집 잠금' : '편집 잠금 해제';
        }
        var toggleBtn = document.getElementById('btn-toggle-edit');
        if (toggleBtn) toggleBtn.addEventListener('click', function(){ setEditable(!unlocked); });
        var printBtn = document.getElementById('btn-print');
        if (printBtn) printBtn.addEventListener('click', function(){ window.print(); });
        setEditable(true);
      })();
    </script>
  </body>
</html>`;
  };

  const handleGenerateAttachAndOpenEmail = async () => {
    if (emailProcessing) return;
    setEmailProcessing(true);
    try {
      const src = document.getElementById('quote-preview-content');
      if (!src) throw new Error('미리보기 내용을 찾을 수 없습니다.');

      // Clone into isolated container
      const wrapper = document.createElement('div');
      wrapper.style.width = '794px';
      wrapper.style.maxWidth = '794px';
      wrapper.style.background = '#ffffff';
      const clone = src.cloneNode(true);
      wrapper.appendChild(clone);

      const safeNameBase = (data?.견적명 || productName || '견적').replace(/["<>:|?*\\/]/g, ' ');
      const stampedName = makeTimestampedName(`${AUTO_QUOTE_PREFIX}${safeNameBase}.pdf`);

      const blob = await generatePdfBlobFromElement(wrapper);
      const { url } = await uploadS3({ file: blob, fileName: stampedName, filePath: ATTACH_PATH });
      const pathOnly = `${ATTACH_PATH}${stampedName}`;
      const newItem = { 첨부파일번호: 0, 파일명: stampedName, 첨부파일경로: pathOnly, url };
      if (typeof onAttachmentAdded === 'function') onAttachmentAdded(newItem);
      if (typeof onOpenEmail === 'function') onOpenEmail(url);
    } catch (e) {
      console.error('PDF 생성/첨부 실패:', e);
      alert(e?.message || 'PDF 생성/첨부에 실패했습니다.');
    } finally {
      setEmailProcessing(false);
    }
  };
  
  useEffect(() => { setShowCost(!!includeCost); }, [includeCost]);
  useEffect(() => { if (!open) { setIncludeProductDetail(false); setProductDetailError(''); } }, [open]);
  
  // Fetch company information
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const { companyNo, userNo } = getUserInfo();
        if (!companyNo || !userNo) return;
        
        const payload = {
          Table1: [
            { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
            { 컬럼명: '@유저번호', 값: Number(userNo), 리스트값: null },
          ],
        };
        const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%9C%A0%EC%A0%80%EC%A0%95%EB%B3%B4';
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload),
        });
        if (response.ok) {
          const result = await response.json();
          const info = result?.table?.[0];
          if (info) {
            setCompanyInfo({
              업체명: info.업체명 || '',
              회사로고: info.회사로고 || '',
              대표자: info.대표자 || '',
              사업자번호: info.사업자번호 || '',
              주소: info.주소 || '',
              전화번호: info.전화번호 || '',
              이메일: info.이메일 || '',
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch company info:', error);
      }
    };
    if (open) fetchCompanyInfo();
  }, [open]);

  const fmt = (v) => {
    const n = Number(v || 0);
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('ko-KR');
  };

  const adultCount = Number(data?.성인수 || 0);
  const childCount = Number(data?.소아수 || 0);
  const infantCount = Number(data?.유아수 || 0);

  const adultPrice = Number(data?.성인판매가 || 0);
  const childPrice = Number(data?.소아판매가 || 0);
  const infantPrice = Number(data?.유아판매가 || 0);

  const adultTotal = adultPrice * adultCount;
  const childTotal = childPrice * childCount;
  const infantTotal = infantPrice * infantCount;

  const totalPeople = adultCount + childCount + infantCount;
  const totalSaleAmount = adultTotal + childTotal + infantTotal;

  // Cost per person aggregation: prefer 세부원가목록 합계, fallback to direct 원가 필드
  const aggregatedPerPersonCost = useMemo(() => {
    const list = Array.isArray(data?.세부원가목록) ? data.세부원가목록 : [];
    const acc = list.reduce((acc, cur) => {
      acc.성인 += Number(cur?.성인 || 0);
      acc.어린이 += Number(cur?.어린이 || 0);
      acc.영유아 += Number(cur?.영유아 || 0);
      return acc;
    }, { 성인: 0, 어린이: 0, 영유아: 0 });
    return acc;
  }, [data?.세부원가목록]);

  const adultCostPer = Number(aggregatedPerPersonCost.성인 || data?.성인원가 || 0);
  const childCostPer = Number(aggregatedPerPersonCost.어린이 || data?.소아원가 || 0);
  const infantCostPer = Number(aggregatedPerPersonCost.영유아 || data?.유아원가 || 0);

  const adultCostTotal = adultCostPer * adultCount;
  const childCostTotal = childCostPer * childCount;
  const infantCostTotal = infantCostPer * infantCount;
  const totalCostAmount = adultCostTotal + childCostTotal + infantCostTotal;

  const period = useMemo(() => {
    const s = data?.시작일 || '';
    const e = data?.종료일 || '';
    if (!s && !e) return '-';
    if (s && !e) return s;
    return `${s} ~ ${e}`;
  }, [data?.시작일, data?.종료일]);

  const today = useMemo(() => new Date().toLocaleDateString('ko-KR'), []);

  useEffect(() => {
    let cancelled = false;
    const loadAndEmbed = async () => {
      if (!open) return;
      if (!includeProductDetail) {
        try {
          const c = productDetailContainerRef.current;
          if (c) c.innerHTML = '';
        } catch {}
        return;
      }
      if (!productNo) {
        setProductDetailError('상품번호가 없습니다.');
        return;
      }
      try {
        setProductDetailLoading(true);
        setProductDetailError('');
        const contentData = await getContentMainList({ productNo });
        // Parse to contentMainList compatible with pdfExportUtil
        const extractHtmlCandidate = (item) => {
          if (!item) return '';
          const keys = ['테그','태그','html','HTML','내용','content','body','desc','description'];
          for (const k of keys) {
            if (item && Object.prototype.hasOwnProperty.call(item, k)) {
              const v = item[k];
              if (typeof v === 'string' && /<[^>]+>/.test(v)) return v;
            }
          }
          if (typeof item === 'string' && /<[^>]+>/.test(item)) return item;
          if (item && typeof item === 'object') {
            for (const v of Object.values(item)) {
              if (typeof v === 'string' && /<[^>]+>/.test(v)) return v;
            }
          }
          return '';
        };
        const contentMainList = [];
        if (Array.isArray(contentData)) {
          for (const row of contentData) {
            let handled = false;
            // 1) Try JSON array in row.컨텐츠 or row.정보
            try {
              const jsonStr = row.컨텐츠 ?? row.정보 ?? '[]';
              const items = JSON.parse(jsonStr);
              if (Array.isArray(items) && items.length > 0) {
                items.forEach((item) => {
                  const titleKey = ['컨텐츠명','콘텐츠명','항목명','제목','타이틀','이름','명','title','name','text']
                    .find(k => item && Object.prototype.hasOwnProperty.call(item, k));
                  const contentName = titleKey ? item[titleKey] : (typeof item === 'string' ? item : row.코드명 || '상세 정보');
                  const contentHtml = extractHtmlCandidate(item);
                  const contentNoKey = ['컨텐츠번호','콘텐츠번호','contentNo','번호','id','no']
                    .find(k => item && Object.prototype.hasOwnProperty.call(item, k));
                  const contentNo = contentNoKey ? item[contentNoKey] : null;
                  if (contentHtml || contentName) {
                    contentMainList.push({
                      콘텐츠명: String(contentName || ''),
                      콘텐츠내용: String(contentHtml || ''),
                      콘텐츠번호: contentNo,
                    });
                  }
                });
                handled = true;
              }
            } catch (_) {}

            if (handled) continue;

            // 2) Fallback: direct HTML in row.컨텐츠 or row.정보
            const raw = String(row?.컨텐츠 ?? row?.정보 ?? '').trim();
            if (raw && /<[^>]+>/.test(raw)) {
              contentMainList.push({
                콘텐츠명: String(row?.코드명 || '상세 정보'),
                콘텐츠내용: raw,
                콘텐츠번호: row?.컨텐츠번호 || row?.콘텐츠번호 || null,
              });
              continue;
            }

            // 3) Fallback: explicit fields 콘텐츠명/콘텐츠내용
            if (row?.콘텐츠내용 || row?.컨텐츠내용) {
              const title = row?.콘텐츠명 || row?.컨텐츠명 || row?.코드명 || '상세 정보';
              const html = row?.콘텐츠내용 || row?.컨텐츠내용 || '';
              contentMainList.push({
                콘텐츠명: String(title),
                콘텐츠내용: String(html),
                콘텐츠번호: row?.컨텐츠번호 || row?.콘텐츠번호 || null,
              });
            }
          }
        }
        let itineraryTypeNo = null;
        try {
          const typeDs = await getItineraryType({ productNo });
          const row = Array.isArray(typeDs?.table) && typeDs.table.length ? typeDs.table[0] : null;
          itineraryTypeNo = row?.일정타입번호 ?? null;
        } catch {}
        let itineraryData = null;
        if (itineraryTypeNo) {
          try {
            itineraryData = await getItineraryDetail({ productNo, itineraryTypeNo });
          } catch {}
        }
        const info = { 상품번호: productNo, 상품명: productName || (data?.견적명 || '여행 상품') };
        const el = buildTravelProductHTMLElement(
          { contentMainList, itineraryData, productInfo: info },
          { hideHeader: true, hideProductInfo: true, hideFooter: true }
        );
        if (!cancelled) {
          const c = productDetailContainerRef.current;
          if (c) {
            c.innerHTML = '';
            if (el) {
              try { el.style.width = '100%'; el.style.maxWidth = '100%'; } catch {}
              c.appendChild(el);
            }
          }
        }
      } catch (e) {
        if (!cancelled) setProductDetailError(e?.message || '상품 상세 로딩 중 오류가 발생했습니다.');
      } finally {
        if (!cancelled) setProductDetailLoading(false);
      }
    };
    loadAndEmbed();
    return () => { cancelled = true; };
  }, [open, includeProductDetail, productNo, productName, data?.견적명]);

  const handlePrint = async () => {
    try {
      const contentEl = document.getElementById('quote-preview-content');
      if (!contentEl) { window.print(); return; }

      const printWin = window.open('', '_blank');
      if (!printWin) { window.print(); return; }

      // Clone styles from current document
      const head = printWin.document.head;
      Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).forEach(node => {
        try {
          const cloned = node.cloneNode(true);
          head.appendChild(cloned);
        } catch {}
      });

      // Basic print-friendly wrapper
      const styleTag = printWin.document.createElement('style');
      styleTag.textContent = `
        @page { margin: 1cm; }
        html, body { padding: 0; margin: 0; }
        #print-root { 
          max-width: none; 
          margin: 0 auto; 
          padding: 20px;
          width: 100%;
        }
        @media print {
          #print-root {
            max-width: none !important;
            width: 100% !important;
            padding: 0 !important;
          }
        }
      `;
      head.appendChild(styleTag);

      const body = printWin.document.body;
      body.innerHTML = `<div id="print-root">${contentEl.outerHTML}</div>`;

      // Wait for resources (images) to load
      const images = Array.from(printWin.document.images || []);
      await Promise.all(images.map(img => new Promise(res => {
        if (img.complete) return res();
        img.onload = img.onerror = () => res();
      })));

      // Give layout a tick then print
      setTimeout(() => {
        printWin.focus();
        printWin.print();
        // Close the window after printing (some browsers ignore onafterprint)
        const closeLater = () => { try { printWin.close(); } catch {} };
        if ('onafterprint' in printWin) {
          printWin.onafterprint = closeLater;
        } else {
          setTimeout(closeLater, 500);
        }
      }, 100);
      // Optionally auto-attach a PDF copy in the background
      if (attachOnPrint) {
        try { await handleGenerateAndAttach(); } catch (e) { console.warn('자동 첨부 실패:', e); }
      }
    } catch {
      window.print();
    }
  };

  const handleGenerateAndAttach = async () => {
    if (attaching) return;
    setAttaching(true);
    try {
      const src = document.getElementById('quote-preview-content');
      if (!src) throw new Error('미리보기 내용을 찾을 수 없습니다.');

      // Clone into an isolated container to avoid mutating the modal
      const wrapper = document.createElement('div');
      wrapper.style.width = '794px';
      wrapper.style.maxWidth = '794px';
      wrapper.style.background = '#ffffff';
      const clone = src.cloneNode(true);
      wrapper.appendChild(clone);

      const safeNameBase = (data?.견적명 || productName || '견적').replace(/["<>:|?*\\/]/g, ' ');
      const stampedName = makeTimestampedName(`${AUTO_QUOTE_PREFIX}${safeNameBase}.pdf`);

      const blob = await generatePdfBlobFromElement(wrapper);
      const { url } = await uploadS3({ file: blob, fileName: stampedName, filePath: ATTACH_PATH });
      const pathOnly = `${ATTACH_PATH}${stampedName}`;
      const newItem = { 첨부파일번호: 0, 파일명: stampedName, 첨부파일경로: pathOnly, url };
      if (typeof onAttachmentAdded === 'function') onAttachmentAdded(newItem);
      alert('견적서 PDF가 첨부파일에 추가되었습니다. 저장을 눌러 확정해주세요.');
    } catch (e) {
      console.error('PDF 생성/첨부 실패:', e);
      alert(e?.message || 'PDF 생성/첨부에 실패했습니다.');
    } finally {
      setAttaching(false);
    }
  };

  const handleGenerateHtmlAndAttach = async () => {
    if (htmlAttaching) return;
    setHtmlAttaching(true);
    try {
      const src = document.getElementById('quote-preview-content');
      if (!src) throw new Error('미리보기 내용을 찾을 수 없습니다.');

      const safeNameBase = (data?.견적명 || productName || '견적').replace(/["<>:|?*\\/]/g, ' ');
      const stampedName = makeTimestampedName(`${AUTO_QUOTE_PREFIX}${safeNameBase}.html`);

      const html = buildStandaloneEditableHtml(src, { title: safeNameBase });
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const { url } = await uploadS3({ file: blob, fileName: stampedName, filePath: ATTACH_PATH });
      const pathOnly = `${ATTACH_PATH}${stampedName}`;
      const newItem = { 첨부파일번호: 0, 파일명: stampedName, 첨부파일경로: pathOnly, url };
      if (typeof onAttachmentAdded === 'function') onAttachmentAdded(newItem);
      alert('편집 가능한 견적서(HTML)가 첨부파일에 추가되었습니다. 저장을 눌러 확정해주세요.');
    } catch (e) {
      console.error('HTML 생성/첨부 실패:', e);
      alert(e?.message || 'HTML 생성/첨부에 실패했습니다.');
    } finally {
      setHtmlAttaching(false);
    }
  };

  return (
    <>
      <style>{`
        @media print {
          @page {
            margin: 1cm;
          }
          body {
            margin: 0;
            padding: 0;
          }
          /* Hide everything by default */
          body * {
            visibility: hidden !important;
          }
          /* Only show the printable content */
          #quote-preview-content, #quote-preview-content * {
            visibility: visible !important;
          }
          #quote-preview-content {
            position: static !important;
            max-width: none !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 20px !important;
            box-shadow: none !important;
            border: none !important;
          }
          /* Remove scroll/size constraints so all pages render */
          #quote-preview-modal-root, #quote-preview-modal-root * {
            overflow: visible !important;
            max-height: none !important;
          }
          /* Improve print color fidelity */
          #quote-preview-modal-root, #quote-preview-modal-root * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
      <div id="quote-preview-modal-root" className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white w-full max-w-4xl max-h-[90vh] rounded-lg shadow-xl border border-gray-200 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 flex-none">
          <div className="text-sm font-semibold text-gray-900">견적서 미리보기</div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-gray-700">
              <input
                type="checkbox"
                className="w-3.5 h-3.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                checked={showCost}
                onChange={(e)=>setShowCost(e.target.checked)}
              />
              <span>원가항목 포함</span>
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-700">
              <input
                type="checkbox"
                className="w-3.5 h-3.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                checked={includeProductDetail}
                onChange={(e)=>setIncludeProductDetail(e.target.checked)}
              />
              <span>상품 상세 포함</span>
            </label>
            <button
              onClick={handleGenerateAndAttach}
              disabled={attaching}
              className="px-3 py-1.5 text-xs rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
            >{attaching ? 'PDF 생성 중...' : 'PDF 생성/첨부'}</button>
            <button
              onClick={handleGenerateHtmlAndAttach}
              disabled={htmlAttaching}
              className="px-3 py-1.5 text-xs rounded border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-50"
            >{htmlAttaching ? 'HTML 생성 중...' : 'HTML 생성/첨부'}</button>
            <button
              onClick={handleGenerateAttachAndOpenEmail}
              disabled={emailProcessing}
              className="px-3 py-1.5 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >{emailProcessing ? '생성 중...' : '이메일 발송'}</button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
            >닫기</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 bg-white">
          <div id="quote-preview-content" className="mx-auto w-full max-w-[794px]">
            {/* Company Header */}
            {companyInfo && (
              <div className="border-b-2 border-blue-600 pb-4 mb-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-2xl font-bold text-gray-900 mb-1">{companyInfo.업체명}</div>
                    <div className="text-xs text-gray-500 mt-1 min-h-[20px] border border-dashed border-gray-300 rounded p-1" data-editable="true" contentEditable="true" style={{ minHeight: '20px', height: 'auto', overflow: 'visible' }}>
                      여기에 추가 텍스트를 입력하세요...
                    </div>
                    {companyInfo.대표자 && (
                      <div className="text-sm text-gray-600 mt-2">대표자: {companyInfo.대표자}</div>
                    )}
                    {companyInfo.사업자번호 && (
                      <div className="text-sm text-gray-600">사업자번호: {companyInfo.사업자번호}</div>
                    )}
                    {companyInfo.주소 && (
                      <div className="text-sm text-gray-600 mt-1">{companyInfo.주소}</div>
                    )}
                    <div className="flex gap-3 mt-1">
                      {companyInfo.전화번호 && (
                        <div className="text-sm text-gray-600">Tel: {companyInfo.전화번호}</div>
                      )}
                      {companyInfo.이메일 && (
                        <div className="text-sm text-gray-600">Email: {companyInfo.이메일}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="border-b border-gray-200 pb-4 mb-6">
              <div className="text-2xl font-bold text-gray-900" data-editable="true">{data?.견적명 || '여행 상품 견적서'}</div>
              <div className="text-xs text-gray-500 mt-1">생성일: {today}</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-gray-800 mb-2">고객 정보</div>
                <div className="space-y-1 text-sm text-gray-700">
                  <div><span className="text-gray-500 mr-2">요청자명</span>{data?.요청자명 || '-'}</div>
                  <div><span className="text-gray-500 mr-2">휴대폰</span>{data?.요청자휴대폰 || '-'}</div>
                  <div><span className="text-gray-500 mr-2">이메일</span>{data?.요청자이메일 || '-'}</div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-gray-800 mb-2">여행 일정 & 인원</div>
                <div className="space-y-1 text-sm text-gray-700">
                  <div><span className="text-gray-500 mr-2">여행기간</span>{period}</div>
                  <div><span className="text-gray-500 mr-2">성인</span>{adultCount}명</div>
                  <div><span className="text-gray-500 mr-2">소아</span>{childCount}명</div>
                  <div><span className="text-gray-500 mr-2">유아</span>{infantCount}명</div>
                  <div className="pt-1 border-t border-gray-100"><span className="text-gray-500 mr-2">총 인원</span><span className="font-semibold text-gray-900">{totalPeople}명</span></div>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 mb-6">
              <div className="text-sm font-semibold text-gray-800 mb-2">견적 내용</div>
              <div className="text-sm text-gray-800 whitespace-pre-wrap" data-editable="true">
                {data?.견적내용 || '-'}
              </div>
            </div>

            {showCost && (
              <div className="border border-gray-200 rounded-lg p-4 mb-6">
                <div className="text-sm font-semibold text-gray-800 mb-3">원가 요약 (1인 기준)</div>
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full text-sm text-gray-800">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600">
                        <th className="text-left font-medium px-3 py-2 border border-gray-200">구분</th>
                        <th className="text-right font-medium px-3 py-2 border border-gray-200">1인 원가</th>
                        <th className="text-right font-medium px-3 py-2 border border-gray-200">인원</th>
                        <th className="text-right font-medium px-3 py-2 border border-gray-200">합계</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-3 py-2 border border-gray-200">성인</td>
                        <td className="px-3 py-2 border border-gray-200 text-right">{fmt(adultCostPer)}원</td>
                        <td className="px-3 py-2 border border-gray-200 text-right">{fmt(adultCount)}</td>
                        <td className="px-3 py-2 border border-gray-200 text-right">{fmt(adultCostTotal)}원</td>
                      </tr>
                      {childCount > 0 && (
                        <tr>
                          <td className="px-3 py-2 border border-gray-200">소아</td>
                          <td className="px-3 py-2 border border-gray-200 text-right">{fmt(childCostPer)}원</td>
                          <td className="px-3 py-2 border border-gray-200 text-right">{fmt(childCount)}</td>
                          <td className="px-3 py-2 border border-gray-200 text-right">{fmt(childCostTotal)}원</td>
                        </tr>
                      )}
                      {infantCount > 0 && (
                        <tr>
                          <td className="px-3 py-2 border border-gray-200">유아</td>
                          <td className="px-3 py-2 border border-gray-200 text-right">{fmt(infantCostPer)}원</td>
                          <td className="px-3 py-2 border border-gray-200 text-right">{fmt(infantCount)}</td>
                          <td className="px-3 py-2 border border-gray-200 text-right">{fmt(infantCostTotal)}원</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="px-3 py-2 border border-gray-200 text-right font-semibold" colSpan={3}>총 원가</td>
                        <td className="px-3 py-2 border border-gray-200 text-right font-semibold">{fmt(totalCostAmount)}원</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {Array.isArray(data?.세부원가목록) && data.세부원가목록.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-gray-800 mb-2">세부 원가 항목 (1인 기준)</div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm text-gray-800">
                        <thead>
                          <tr className="bg-gray-50 text-gray-600">
                            <th className="text-left font-medium px-3 py-2 border border-gray-200">항목명</th>
                            <th className="text-right font-medium px-3 py-2 border border-gray-200">성인</th>
                            {childCount > 0 && (
                              <th className="text-right font-medium px-3 py-2 border border-gray-200">소아</th>
                            )}
                            {infantCount > 0 && (
                              <th className="text-right font-medium px-3 py-2 border border-gray-200">유아</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {data.세부원가목록.map((row, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-2 border border-gray-200">{row?.원가항목 || '-'}</td>
                              <td className="px-3 py-2 border border-gray-200 text-right">{fmt(row?.성인)}</td>
                              {childCount > 0 && (
                                <td className="px-3 py-2 border border-gray-200 text-right">{fmt(row?.어린이)}</td>
                              )}
                              {infantCount > 0 && (
                                <td className="px-3 py-2 border border-gray-200 text-right">{fmt(row?.영유아)}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="border border-gray-200 rounded-lg p-4 mb-6">
              <div className="text-sm font-semibold text-gray-800 mb-3">가격 요약 (판매가 기준)</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-gray-800">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600">
                      <th className="text-left font-medium px-3 py-2 border border-gray-200">구분</th>
                      <th className="text-right font-medium px-3 py-2 border border-gray-200">1인 가격</th>
                      <th className="text-right font-medium px-3 py-2 border border-gray-200">인원</th>
                      <th className="text-right font-medium px-3 py-2 border border-gray-200">합계</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-3 py-2 border border-gray-200">성인</td>
                      <td className="px-3 py-2 border border-gray-200 text-right" data-editable="true">{fmt(adultPrice)}원</td>
                      <td className="px-3 py-2 border border-gray-200 text-right">{fmt(adultCount)}</td>
                      <td className="px-3 py-2 border border-gray-200 text-right">{fmt(adultTotal)}원</td>
                    </tr>
                    {childCount > 0 && (
                      <tr>
                        <td className="px-3 py-2 border border-gray-200">소아</td>
                        <td className="px-3 py-2 border border-gray-200 text-right" data-editable="true">{fmt(childPrice)}원</td>
                        <td className="px-3 py-2 border border-gray-200 text-right">{fmt(childCount)}</td>
                        <td className="px-3 py-2 border border-gray-200 text-right">{fmt(childTotal)}원</td>
                      </tr>
                    )}
                    {infantCount > 0 && (
                      <tr>
                        <td className="px-3 py-2 border border-gray-200">유아</td>
                        <td className="px-3 py-2 border border-gray-200 text-right" data-editable="true">{fmt(infantPrice)}원</td>
                        <td className="px-3 py-2 border border-gray-200 text-right">{fmt(infantCount)}</td>
                        <td className="px-3 py-2 border border-gray-200 text-right">{fmt(infantTotal)}원</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="px-3 py-2 border border-gray-200 text-right font-semibold" colSpan={3}>총 판매가</td>
                      <td className="px-3 py-2 border border-gray-200 text-right font-semibold">{fmt(totalSaleAmount)}원</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {Array.isArray(data?.견적첨부파일리스트) && data.견적첨부파일리스트.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4 mb-6">
                <div className="text-sm font-semibold text-gray-800 mb-2">첨부파일</div>
                <ul className="list-disc pl-5 text-sm text-gray-800 space-y-1">
                  {data.견적첨부파일리스트.map((f, idx) => (
                    <li key={idx} className="break-all">
                      {f?.파일명 || f?.name || '첨부파일'}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {includeProductDetail && (
              <div className="mt-8">
                <div className="text-sm font-semibold text-gray-800 mb-3">상품 상세</div>
                {productDetailError && (
                  <div className="mb-2 text-xs text-red-600">{productDetailError}</div>
                )}
                {productDetailLoading && (
                  <div className="mb-2 text-xs text-gray-600">불러오는 중...</div>
                )}
                <div ref={productDetailContainerRef} className="w-full" />
              </div>
            )}

            {/* Company Footer */}
            {companyInfo && (
              <div className="border-t-2 border-gray-300 pt-4 mt-8">
                <div className="text-center">
                  <div className="text-sm font-semibold text-gray-800 mb-2">{companyInfo.업체명}</div>
                  <div className="text-xs text-gray-600 space-y-0.5">
                    {companyInfo.주소 && <div>{companyInfo.주소}</div>}
                    <div className="flex justify-center gap-4">
                      {companyInfo.전화번호 && <span>Tel: {companyInfo.전화번호}</span>}
                      {companyInfo.이메일 && <span>Email: {companyInfo.이메일}</span>}
                    </div>
                    {companyInfo.사업자번호 && (
                      <div className="mt-1">사업자번호: {companyInfo.사업자번호}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
